import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material'
import React, { useState } from 'react'
import { useCustomLessonCountForClass } from 'shared/api/customLessons'
import { resetCustomLessonsForClass } from 'shared/api/mutations'
import { useNotify } from 'shared/ui/Notify'

interface ResetCustomLessonProps {
  classId: number
  className: string
}

const RESET_FAILED_MESSAGE = 'Failed to reset custom lessons.'
const CONFIRMATION_INPUT = 'Confirm'
const DIALOG_TITLE = 'Reset Custom Lessons'

export function ResetCustomLesson({
  classId,
  className,
}: ResetCustomLessonProps) {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isResetting, setIsResetting] = useState(false)
  const [confirmationInput, setConfirmationInput] = useState('')
  const [customLessonCount, refreshCustomLessonCount] =
    useCustomLessonCountForClass(classId)
  const notify = useNotify()

  const handleClick = () => {
    setIsModalOpen(true)
  }

  const handleClose = () => {
    setIsModalOpen(false)
    setConfirmationInput('')
  }

  const handleReset = async () => {
    setIsResetting(true)
    try {
      const { data, errors } = await resetCustomLessonsForClass(classId)
      if (!data || errors) {
        throw new Error(RESET_FAILED_MESSAGE)
      }
      // Refresh the custom lesson count after successful reset
      refreshCustomLessonCount()
      handleClose()
      setIsResetting(false)
      notify('Custom lessons reset successfully.', { type: 'success' })
    } catch (error) {
      console.error('Failed to reset custom lessons:', error)
      notify(RESET_FAILED_MESSAGE, { type: 'error' })
      setIsResetting(false)
    }
  }

  const isResetEnabled = confirmationInput === CONFIRMATION_INPUT

  return (
    <>
      <Button variant="outlined" color="primary" onClick={handleClick}>
        {DIALOG_TITLE}
      </Button>

      <Dialog open={isModalOpen} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle>{DIALOG_TITLE}</DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            <strong>Custom Lesson Count:</strong> {customLessonCount}
          </Typography>
          {customLessonCount > 0 && (
            <>
              <Typography variant="body2" color="error" sx={{ mt: 2, mb: 1 }}>
                Warning: This action cannot be undone.
              </Typography>
              <TextField
                fullWidth
                label='Type "Confirm" to reset custom lessons'
                value={confirmationInput}
                onChange={(e) => setConfirmationInput(e.target.value)}
                variant="outlined"
                size="small"
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" color="primary" onClick={handleClose}>
            Back
          </Button>
          {customLessonCount > 0 && (
            <Button
              variant="contained"
              color="error"
              onClick={handleReset}
              disabled={isResetting || !isResetEnabled}
            >
              {isResetting ? 'Resetting...' : 'Reset'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  )
}
